import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

interface ContactFormInputs {
  name: string;
  email: string;
  company?: string;
  message: string;
  botField?: string;
}

const inputCls = (hasError?: boolean) =>
  `mt-1.5 block w-full rounded-xl bg-slate-50 border ${
    hasError ? 'border-red-400' : 'border-slate-200'
  } text-slate-900 px-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-em/30 focus:border-em transition-all`;

const Contact: React.FC = () => {
  const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<ContactFormInputs>();
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [loadedAt] = useState(() => Date.now());
  const CONTACT_ENDPOINT = import.meta.env.VITE_CONTACT_ENDPOINT || '/api/contact';

  // Handles validation, anti-spam checks, and posts to the configured endpoint.
  const onSubmit = async (data: ContactFormInputs) => {
    if (data.botField && data.botField.trim().length > 0) return;
    if (Date.now() - loadedAt < 1500) {
      setError('message', { type: 'manual', message: 'Please take a brief moment before submitting.' });
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    try {
      const payload = { name: data.name, email: data.email, company: data.company, message: data.message, destination: 'info@novaterraconsulting.net' };
      const response = await fetch(CONTACT_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      reset();
      setStatus('success');
    } catch (error) {
      console.error('Contact submission failed', error);
      setStatus('error');
      setError('message', { type: 'manual', message: 'We could not send your message. Please try again shortly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

          {/* Left sidebar */}
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-em-light border border-em-border text-xs text-em-deeper font-semibold">
                Let's Start a Conversation
              </span>
              <h2 className="text-3xl font-bold text-slate-900 leading-tight">
                Tell us about<br />your goals.
              </h2>
            </div>
            <p className="text-slate-500 leading-relaxed">
              Share where you are today and where you need to be. We respond within one business day with a suggested path forward.
            </p>

            <div className="rounded-2xl border border-em-border bg-em-light p-5 space-y-3">
              <p className="text-[10px] text-em-dark font-bold uppercase tracking-widest">What to expect</p>
              {[
                { id: 'call',     text: 'A short call to understand your goals and timelines', accent: 'bg-em' },
                { id: 'proposal', text: 'A concise proposal aligned to outcomes', accent: 'bg-teal-dark' },
                { id: 'spam',     text: 'No spam, ever', accent: 'bg-em' },
              ].map((item) => (
                <div key={item.id} className="flex items-start gap-2.5">
                  <div className={`mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0 ${item.accent}`} />
                  <p className="text-sm text-em-deeper">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="lg:col-span-3 relative rounded-3xl border border-slate-100 bg-white shadow-card p-5 sm:p-8 space-y-5 overflow-hidden"
          >
            <div className="absolute inset-x-0 top-0 h-0.5 gradient-em" />
            <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-em/[0.05] blur-2xl pointer-events-none" />

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Name *</label>
                <input type="text" id="name" {...register('name', { required: 'Name is required' })} className={inputCls(!!errors.name)} />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
              </div>
              <div>
                <label htmlFor="email" className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Email *</label>
                <input
                  type="email"
                  id="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.(?:com|net|org|io|gov|ai|co|us|uk|edu|info|biz)$/i,
                      message: 'Use a valid email domain (.com, .net, .io, .org, .gov, .ai, etc.)',
                    },
                  })}
                  className={inputCls(!!errors.email)}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              </div>
            </div>

            <div className="relative">
              <label htmlFor="company" className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">Company</label>
              <input type="text" id="company" {...register('company')} className={inputCls()} />
            </div>

            <div className="relative">
              <label htmlFor="message" className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">How can we help you? *</label>
              <textarea
                id="message"
                {...register('message', { required: 'Message is required', minLength: { value: 10, message: 'Please share a bit more detail (10+ characters).' } })}
                className={inputCls(!!errors.message)}
                rows={5}
              />
              {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message.message}</p>}
            </div>

            {/* Honeypot field to reduce bot spam */}
            <div className="hidden">
              <label htmlFor="botField">Leave this field empty</label>
              <input id="botField" type="text" tabIndex={-1} autoComplete="off" {...register('botField')} />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="relative w-full btn-shimmer text-white py-3.5 rounded-full text-sm font-bold shadow-glow-em hover:shadow-glow-em-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending...' : 'Send Message'}
            </button>

            {status === 'success' && <p className="text-em-deeper text-sm text-center font-medium">Thanks! Your message was sent to info@novaterraconsulting.net.</p>}
            {status === 'error' && <p className="text-red-500 text-sm text-center">There was a problem sending your message. Please try again.</p>}
          </form>

        </div>
      </div>
    </section>
  );
};

export default Contact;
